'use client'

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { X, ArrowRight, ArrowLeft, FileText, Bot, BarChart2, Check, LayoutGrid, PlusCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TourStep {
  id: string
  title: string
  description: string
  target: string // CSS selector or 'navigation' | 'page'
  icon: React.ComponentType<{ className?: string }>
  position?: 'top' | 'bottom' | 'left' | 'right'
  highlightCss?: string
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'room-list',
    title: 'Your Rooms & Shared Rooms',
    description: 'This is where you can view and access all your rooms, including those shared with you.',
    target: '[data-tour="rooms-container"]',
    icon: LayoutGrid,
    position: 'top',
    highlightCss: 'background-color: #f8fafc; border-radius: 12px; padding: 12px; margin: -12px;',
  },
  {
    id: 'create-room',
    title: 'Create Room',
    description: 'Click here to create a new room. You can choose from existing templates or let our AI assist you.',
    target: '[data-tour="create-room"]',
    icon: PlusCircle,
    position: 'left',
    action: {
      label: 'Create Room',
      onClick: () => {
        const button = document.querySelector('[data-tour="create-room"]') as HTMLElement
        button?.click()
      },
    },
  },
  {
    id: 'content-library',
    title: 'Content Library',
    description: 'Manage all your sales content, images, and room templates in one central place.',
    target: '[data-tour="library"]',
    icon: FileText,
    position: 'right',
  },
  {
    id: 'ai-agents',
    title: 'AI Agents',
    description: 'Use AI Agents to automate workflows and answer customer queries based on your room data.',
    target: '[data-tour="ai-agents"]',
    icon: Bot,
    position: 'right',
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Track engagement and performance metrics across all your rooms.',
    target: '[data-tour="analytics"]',
    icon: BarChart2,
    position: 'right',
  },
]

interface FeatureTourProps {
  tourId?: string // Tour ID (defaults to 'dashboard')
  steps?: TourStep[] // Custom steps (defaults to TOUR_STEPS)
  autoStart?: boolean // Auto-start based on DB (defaults to true)
  onComplete?: () => void
  onSkip?: () => void
}

export function FeatureTour({
  tourId = 'dashboard',
  steps: customSteps,
  autoStart = false,
  onComplete,
  onSkip
}: FeatureTourProps) {
  const router = useRouter()
  const tourSteps = customSteps || TOUR_STEPS
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  const updateStepPosition = useCallback((stepIndex: number) => {
    const step = tourSteps[stepIndex]
    if (!step) return

    if (step.target === 'page') {
      // Center on page
      setTargetElement(null)
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      })
      return
    }

    // Find target element
    const element = document.querySelector(step.target) as HTMLElement
    if (element) {
      setTargetElement(element)
      // Use getBoundingClientRect which gives viewport-relative coordinates
      // Ensure we're getting the exact viewport position without any offset
      const rect = element.getBoundingClientRect()

      // Get actual viewport dimensions
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      const cardWidth = 420
      const cardHeight = 300 // Approximate height
      const spacing = 20

      // Calculate position based on step position preference
      // Use viewport-relative coordinates directly from getBoundingClientRect
      switch (step.position) {
        case 'bottom':
          setPosition({
            top: rect.bottom + spacing,
            left: Math.max(spacing, Math.min(rect.left + rect.width / 2, viewportWidth - cardWidth - spacing)),
          })
          break
        case 'right':
          // For navigation items on the left sidebar, position to the right of the sidebar
          // Check if there's enough space on the right
          const spaceOnRight = viewportWidth - rect.right
          if (spaceOnRight >= cardWidth + spacing) {
            // Enough space on right
            setPosition({
              top: Math.max(spacing, Math.min(rect.top + rect.height / 2 - cardHeight / 2, viewportHeight - cardHeight - spacing)),
              left: rect.right + spacing,
            })
          } else if (rect.left >= cardWidth + spacing) {
            // Not enough space on right, position on left instead
            setPosition({
              top: Math.max(spacing, Math.min(rect.top + rect.height / 2 - cardHeight / 2, viewportHeight - cardHeight - spacing)),
              left: rect.left - cardWidth - spacing,
            })
          } else {
            // Not enough space on either side, position below
            setPosition({
              top: rect.bottom + spacing,
              left: Math.max(spacing, Math.min(rect.left + rect.width / 2, viewportWidth - cardWidth - spacing)),
            })
          }
          break
        case 'left':
          // Check if there's enough space on the left
          if (rect.left - cardWidth - spacing >= 0) {
            setPosition({
              top: Math.max(spacing, Math.min(rect.top + rect.height / 2 - cardHeight / 2, viewportHeight - cardHeight - spacing)),
              left: rect.left - cardWidth - spacing,
            })
          } else {
            // Not enough space on left, position on right instead
            setPosition({
              top: Math.max(spacing, Math.min(rect.top + rect.height / 2 - cardHeight / 2, viewportHeight - cardHeight - spacing)),
              left: Math.min(viewportWidth - cardWidth - spacing, rect.right + spacing),
            })
          }
          break
        default: // top
          setPosition({
            top: Math.max(spacing, rect.top - cardHeight - spacing),
            left: Math.max(spacing, Math.min(rect.left + rect.width / 2, viewportWidth - cardWidth - spacing)),
          })
      }
    } else {
      // Element not found, center on page
      setTargetElement(null)
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      })
    }
  }, [tourSteps])

  // Expose start function for manual triggers
  const startTour = useCallback(() => {
    setIsVisible(true)
    setCurrentStep(0)
    setTimeout(() => {
      updateStepPosition(0)
    }, 100)
  }, [updateStepPosition])

  const searchParams = useSearchParams()

  // Expose startTour via window for manual triggers and check URL params
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>)[`startTour_${tourId}`] = startTour

      // Check query param
      const tourParam = searchParams.get('tour')
      if (tourParam === tourId) {
        startTour()
      }

      // Also listen for custom events
      const handleStartTour = (event: CustomEvent) => {
        if (event.detail?.tourId === tourId) {
          startTour()
        }
      }
      window.addEventListener('start-tour', handleStartTour as EventListener)

      return () => {
        delete (window as unknown as Record<string, unknown>)[`startTour_${tourId}`]
        window.removeEventListener('start-tour', handleStartTour as EventListener)
      }
    }
  }, [tourId, startTour, searchParams])

  // First-visit auto-start (skipped when URL already requests a tour)
  useEffect(() => {
    if (!autoStart) return
    const tourParam = searchParams.get('tour')
    if (tourParam) return

    let cancelled = false

    const checkAndStart = async () => {
      try {
        const res = await fetch(`/api/onboarding/tour-status?tourId=${tourId}`)
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (data.shouldShowTour && !cancelled) {
          startTour()
        }
      } catch (error) {
        console.error('Error checking tour status:', error)
      }
    }

    const timer = setTimeout(checkAndStart, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [autoStart, tourId, searchParams, startTour])

  // Check for modals/dialogs and pause tour if they're open
  useEffect(() => {
    if (!isVisible) return

    const checkForModals = () => {
      // Get tour elements to exclude
      const tourCard = document.querySelector('[data-tour-card]')
      const tourOverlay = document.querySelector('[data-tour-overlay]')

      // Check for actual modals/dialogs (not the tour)
      // Look for elements with role="dialog" that are NOT the tour card
      const allDialogs = document.querySelectorAll('[role="dialog"]')
      const otherDialogs = Array.from(allDialogs).filter(
        (dialog) => {
          // Exclude tour card
          if (tourCard && (dialog === tourCard || dialog.contains(tourCard) || tourCard.contains(dialog))) {
            return false
          }
          // Exclude if it's inside the tour overlay
          if (tourOverlay && tourOverlay.contains(dialog)) {
            return false
          }
          return true
        }
      )

      // Check for backdrop overlays that are NOT the tour overlay
      // Look for backdrops with z-index higher than tour (z-40) - modals are usually z-50
      const allBackdrops = document.querySelectorAll('.fixed.inset-0')
      const otherBackdrops = Array.from(allBackdrops).filter(
        (backdrop) => {
          // Exclude tour overlay
          if (backdrop === tourOverlay || (tourOverlay && tourOverlay.contains(backdrop))) {
            return false
          }
          // Only consider backdrops with z-index >= 50 (actual modals)
          const zIndex = window.getComputedStyle(backdrop as Element).zIndex
          const zIndexNum = zIndex === 'auto' ? 0 : parseInt(zIndex)
          return zIndexNum >= 50
        }
      )

      // If there are actual modals open (not the tour), pause the tour
      const hasModals = otherDialogs.length > 0 || otherBackdrops.length > 0

      if (hasModals && !isPaused) {
        setIsPaused(true)
        setIsVisible(false)
      } else if (!hasModals && isPaused) {
        // Resume tour when modal closes
        setIsPaused(false)
        // Small delay to ensure modal is fully closed
        setTimeout(() => {
          setIsVisible(true)
        }, 300)
      }
    }

    // Only start checking after a delay to avoid false positives on tour start
    let intervalId: NodeJS.Timeout | null = null
    const timeout = setTimeout(() => {
      intervalId = setInterval(checkForModals, 500)
    }, 1000)

    return () => {
      clearTimeout(timeout)
      if (intervalId) clearInterval(intervalId)
    }
  }, [isVisible, isPaused])

  const markTourCompleted = useCallback(async (skipped: boolean) => {
    try {
      await fetch('/api/onboarding/tour-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tourId,
          skipped,
        }),
      })
    } catch (error) {
      console.error('Error marking tour complete:', error)
    }
  }, [tourId])

  const handleComplete = useCallback(async () => {
    setIsVisible(false)
    await markTourCompleted(false) // completed
    onComplete?.()
  }, [markTourCompleted, onComplete])

  useEffect(() => {
    if (isVisible && !isPaused) {
      // Check if target element exists
      const step = tourSteps[currentStep]
      if (step && step.target !== 'page') {
        const element = document.querySelector(step.target) as HTMLElement
        if (!element) {
          // Element not found - only skip if it truly doesn't exist (not just hidden)
          // Wait a bit to see if it appears (e.g., if navigation is collapsed)
          const checkAgain = setTimeout(() => {
            const elementAgain = document.querySelector(step.target) as HTMLElement
            if (!elementAgain) {
              // Still not found, skip to next step
              if (currentStep < tourSteps.length - 1) {
                setCurrentStep(currentStep + 1)
              } else {
                handleComplete()
              }
            }
          }, 1000)

          return () => clearTimeout(checkAgain)
        }

        // Check if element is visible (not display:none or visibility:hidden)
        const rect = element.getBoundingClientRect()
        const style = window.getComputedStyle(element)
        const isElementVisible = rect.width > 0 && rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0'

        // Don't skip if element exists but is temporarily hidden - just wait
        // The modal detection will handle pausing
        if (!isElementVisible) {
          // Element exists but not visible - wait a bit, might be in collapsed nav
          return
        }
      }

      // Small delay to ensure DOM is ready
      setTimeout(() => {
        updateStepPosition(currentStep)

        // Scroll to element if needed
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }, [currentStep, isVisible, isPaused, updateStepPosition, targetElement, tourSteps, handleComplete])

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = useCallback(async () => {
    setIsVisible(false)
    await markTourCompleted(true) // skipped
    onSkip?.()
  }, [markTourCompleted, onSkip])


  const handleAction = () => {
    const step = tourSteps[currentStep]
    if (step.action) {
      if (step.action.onClick) {
        step.action.onClick()
      } else if (step.action.href) {
        router.push(step.action.href)
      }
    }
    handleNext()
  }

  if (!isVisible || !position) return null

  const step = tourSteps[currentStep]
  const progress = ((currentStep + 1) / tourSteps.length) * 100
  const Icon = step.icon

  return createPortal(
    <>
      {/* Overlay - no blur to keep navigation items clear */}
      <div
        data-tour-overlay
        className="fixed bg-black/60 z-40 transition-opacity"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
        }}
        onClick={handleSkip}
      />

      {/* Highlight overlay for target element */}
      {targetElement && (() => {
        // Use getBoundingClientRect which gives exact viewport coordinates
        const rect = targetElement.getBoundingClientRect()
        return (
          <>
            {/* Highlight border - prominent blue border with glow */}
            <div
              className="fixed pointer-events-none"
              style={{
                top: `${rect.top - 4}px`,
                left: `${rect.left - 4}px`,
                width: `${rect.width + 8}px`,
                height: `${rect.height + 8}px`,
                zIndex: 45,
                border: '3px solid rgb(59, 130, 246)',
                borderRadius: '8px',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 0 4px rgba(59, 130, 246, 0.4), 0 0 20px rgba(59, 130, 246, 0.3)',
                backgroundColor: 'transparent',
              }}
            />
            {/* Ensure target element is visible and above overlay */}
            <style>{`
              ${step.target} {
                position: relative !important;
                z-index: 46 !important;
                isolation: isolate !important;
                ${step.highlightCss || ''}
              }
            `}</style>
          </>
        )
      })()}

      {/* Tour Card */}
      <Card
        data-tour-card
        className={cn(
          'fixed z-50 w-[420px] max-w-[calc(100vw-2rem)] shadow-2xl border-2',
          'bg-white'
        )}
        style={{
          top: step.target === 'page'
            ? '50%'
            : step.position === 'right' || step.position === 'left'
              ? `${position.top}px`
              : `${position.top}px`,
          left: step.target === 'page'
            ? '50%'
            : `${position.left}px`,
          transform: step.target === 'page'
            ? 'translate(-50%, -50%)'
            : step.position === 'right' || step.position === 'left'
              ? 'translate(0, 0)'
              : 'translateX(-50%)',
        }}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">
                  {step.title}
                </CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-500">
                    Step {currentStep + 1} of {tourSteps.length}
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-400 hover:text-slate-600"
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <Progress value={progress} className="h-1.5" />

          <CardDescription className="text-sm text-slate-600 leading-relaxed">
            {step.description}
          </CardDescription>

          {step.action && (
            <Button
              onClick={handleAction}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              {step.action.label}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="text-slate-600"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>

            <div className="flex items-center gap-2">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-1.5 w-1.5 rounded-full transition-colors',
                    index === currentStep
                      ? 'bg-blue-600 w-6'
                      : index < currentStep
                        ? 'bg-blue-300'
                        : 'bg-slate-200'
                  )}
                />
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={currentStep === tourSteps.length - 1 ? handleComplete : handleNext}
              className="text-slate-600"
            >
              {currentStep === tourSteps.length - 1 ? (
                <>
                  Complete
                  <Check className="ml-1 h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>,
    document.body
  )
}

